import ExcelJS from 'exceljs';
import azureDevOpsService from './azureDevOpsService';

class ApiCoverageService {
  async loadCoverageData() {
    try {
      const pat = azureDevOpsService.getPat();
      const response = await fetch('/api/coverage-data', {
        headers: pat ? { 'X-Azure-Devops-Pat': pat } : {},
      });
      if (!response.ok) throw new Error('Failed to fetch api_coverage.xlsx');
      const lastModified = response.headers.get('last-modified');
      
      const arrayBuffer = await response.arrayBuffer();
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(arrayBuffer);

      const data = {
        metadata: {
          source: 'api-coverage.xlsx',
          scopeVersion: 'develop',
          lastUpdated: lastModified || null,
          isSnapshot: true
        },
        sourceComparison: this.parseSourceComparison(workbook.getWorksheet('source_comparison')),
        globalSwagger: this.parseGlobalSwagger(workbook.getWorksheet('global_swagger')),
        globalSwaggerTag: this.parseGlobalSwaggerTag(workbook.getWorksheet('global_swagger_tag')),
        swaggers: {}
      };

      // Process each swagger
      const swaggerKeys = ['adm', 'dp', 'env', 'exp', 'kb', 'agentic', 'osint'];
      for (const key of swaggerKeys) {
        const tags = this.parseSwaggerTags(workbook.getWorksheet(`swagger_${key}_tags`));
        const details = this.parseSwaggerDetails(workbook.getWorksheet(`swagger_${key}_details`));
        
        // Aggregate ReadyAPI and RobotFramework calls by tag
        const tagsWithCalls = tags.map(tag => {
          const tagDetails = details.filter(d => d.feature === tag.feature);
          const readyApiCalls = tagDetails.reduce((sum, d) => sum + d.readyApiCalls, 0);
          const robotframeworkCalls = tagDetails.reduce((sum, d) => sum + d.robotframeworkCalls, 0);
          return {
            ...tag,
            readyApiCalls,
            robotframeworkCalls
          };
        });

        data.swaggers[key] = {
          tags: tagsWithCalls,
          details
        };
      }

      return data;
    } catch (error) {
      console.error('Error loading coverage data:', error);
      throw error;
    }
  }

  parseSourceComparison(worksheet) {
    const data = [];
    if (!worksheet) return data;

    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber === 1) return; // Skip header
      const values = row.values;
      if (values && values[1]) {
        // Col1: source, Col2: total_endpoints, Col3: tested_endpoints, 
        // Col4: total_calls, Col5: not_tested_endpoints, Col6: coverage_pct
        data.push({
          tool: values[1],
          totalEndpoints: parseInt(values[2]) || 0,
          testedEndpoints: parseInt(values[3]) || 0,
          totalCalls: parseInt(values[4]) || 0,
          notTestedEndpoints: parseInt(values[5]) || 0,
          coverage: parseFloat(values[6]) || 0
        });
      }
    });
    return data;
  }

  parseGlobalSwagger(worksheet) {
    const data = [];
    if (!worksheet) return data;

    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber === 1) return;
      const values = row.values;
      if (values && values[1]) {
        const totalEndpoints = Number(values[2]);
        const testedEndpoints = Number(values[3]);
        const calls_243 = Number(values[4]);
        const calls_252 = Number(values[5]);
        const calls_261 = Number(values[6]);
        const notTestedEndpoints = Number(values[7]);
        const coverage_243 = Number(values[8]);
        const coverage_252 = Number(values[9]);
        const coverage_261 = Number(values[10]);

        // Ignore chart helper blocks embedded in the worksheet
        if (
          !Number.isFinite(totalEndpoints) ||
          !Number.isFinite(testedEndpoints) ||
          !Number.isFinite(notTestedEndpoints)
        ) {
          return;
        }

        // Col1: swagger, Col2: total_endpoints, Col3: tested_endpoints,
        // Col4: total_calls_24.3, Col5: total_calls_25.2, Col6: total_calls_26.1,
        // Col7: not_tested_endpoints, Col8: coverage_24.3, Col9: coverage_25.2, Col10: coverage_26.1
        data.push({
          swagger: values[1],
          totalEndpoints,
          testedEndpoints,
          calls_243,
          calls_252,
          calls_261,
          notTestedEndpoints,
          coverage_243,
          coverage_252,
          coverage_261,
          coverage: coverage_261
        });
      }
    });
    return data;
  }

  parseGlobalSwaggerTag(worksheet) {
    const data = [];
    if (!worksheet) return data;

    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber === 1) return;
      const values = row.values;
      if (values && values[1]) {
        // Col1: swagger, Col2: feature, Col3: total_endpoints, Col4: tested_endpoints,
        // Col5: total_calls, Col6: not_tested_endpoints, Col7: coverage_pct
        data.push({
          swagger: values[1],
          tag: values[2],
          totalEndpoints: parseInt(values[3]) || 0,
          coveredEndpoints: parseInt(values[4]) || 0,
          totalCalls: parseInt(values[5]) || 0,
          notTestedEndpoints: parseInt(values[6]) || 0,
          coverage: parseFloat(values[7]) || 0
        });
      }
    });
    return data;
  }

  parseSwaggerTags(worksheet) {
    const data = [];
    if (!worksheet) return data;

    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber === 1) return;
      const values = row.values;
      if (values && values[1]) {
        // Col1: feature, Col2: total_endpoints, Col3: tested_endpoints,
        // Col4: total_calls, Col5: not_tested_endpoints, Col6: coverage_pct
        data.push({
          feature: values[1],
          totalEndpoints: parseInt(values[2]) || 0,
          testedEndpoints: parseInt(values[3]) || 0,
          totalCalls: parseInt(values[4]) || 0,
          notTestedEndpoints: parseInt(values[5]) || 0,
          coverage: parseFloat(values[6]) || 0
        });
      }
    });
    return data;
  }

  parseSwaggerDetails(worksheet) {
    const data = [];
    if (!worksheet) return data;

    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber === 1) return;
      const values = row.values;
      if (values && values[1]) {
        // Col1: item, Col2: feature, Col3: swagger, Col4: readyapi_count,
        // Col5: robot_count, Col6: robot_match_type, Col7: count (total),
        // Col8: tested, Col9: tested_readyapi, Col10: tested_robot
        data.push({
          endpoint: values[1],
          feature: values[2],
          swagger: values[3],
          readyApiCalls: parseInt(values[4]) || 0,
          robotframeworkCalls: parseInt(values[5]) || 0,
          totalCalls: parseInt(values[7]) || 0,
          isCovered: values[8] === true || values[8] === 'TRUE' || values[8] === 1,
          testedReadyApi: parseInt(values[9]) || 0,
          testedRobot: parseInt(values[10]) || 0
        });
      }
    });
    return data;
  }
}

export default new ApiCoverageService();
